import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Building2, X, Clock, Banknote } from 'lucide-react';
import { stockService } from '../../services/api';
import type { Fournisseur } from '../../types';
import { formatCurrency } from '../../utils/formatters';

const num = (s: string | number | null | undefined) => Number(s ?? 0);

const FournisseursTiers: React.FC = () => {
  const [selected, setSelected] = useState<Fournisseur | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['fournisseurs', 'tiers'],
    queryFn: () => stockService.getFournisseurs().then((r) => r.data),
  });

  const fournisseurs: Fournisseur[] = Array.isArray(data) ? data : (data?.results ?? []);

  if (isLoading) {
    return <div className="p-6 text-gray-500">Chargement des fournisseurs…</div>;
  }

  return (
    <div className="w-full">
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                <th className="px-4 py-2">Fournisseur</th>
                <th className="px-4 py-2">Type</th>
                <th className="px-4 py-2">Délai paiement</th>
                <th className="px-4 py-2 text-right">Solde dû</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {fournisseurs.map((f: any) => (
                <tr key={f.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <span className="p-1.5 rounded-lg bg-gray-100 text-gray-500"><Building2 className="h-4 w-4" /></span>
                      <div>
                        <p className="text-sm font-medium text-gray-800">{f.nom}</p>
                        {f.contact && <p className="text-xs text-gray-400">{f.contact}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-600">{f.type_display || f.type_fournisseur}</td>
                  <td className="px-4 py-2 text-sm text-gray-600">{f.delai_paiement_jours ?? 30} j</td>
                  <td className={`px-4 py-2 text-sm text-right font-semibold ${num(f.solde_du) > 0 ? 'text-red-600' : 'text-gray-700'}`}>
                    {formatCurrency(num(f.solde_du))}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => setSelected(f)}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Voir le compte
                    </button>
                  </td>
                </tr>
              ))}
              {fournisseurs.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">Aucun fournisseur.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selected && <CompteFournisseurDrawer fournisseur={selected} onClose={() => setSelected(null)} />}
    </div>
  );
};

const CompteFournisseurDrawer: React.FC<{ fournisseur: Fournisseur; onClose: () => void }> = ({ fournisseur, onClose }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['fournisseur', 'compte', (fournisseur as any).id],
    queryFn: () => stockService.getFournisseurCompte((fournisseur as any).id).then((r) => r.data),
  });

  const b = data?.balance_agee;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <div className="bg-white w-full max-w-xl h-full overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 text-white px-5 py-4 flex items-center justify-between">
          <div>
            <h3 className="font-semibold">{(fournisseur as any).nom}</h3>
            <p className="text-sm text-white/70">Compte fournisseur</p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white"><X className="h-5 w-5" /></button>
        </div>

        {isLoading || !data ? (
          <div className="p-6 text-gray-500">Chargement du compte…</div>
        ) : (
          <div className="p-5 space-y-5">
            <div className="bg-red-50 rounded-xl p-4 text-center">
              <p className="text-sm text-red-600">Solde dû</p>
              <p className="text-2xl font-bold text-red-700">{formatCurrency(num(data.solde_du))}</p>
            </div>

            {/* Balance âgée */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                <Clock className="h-4 w-4" /> Balance âgée
              </h4>
              <div className="grid grid-cols-4 gap-2 text-center">
                {[
                  { k: '0_30', label: '0-30 j' },
                  { k: '31_60', label: '31-60 j' },
                  { k: '61_90', label: '61-90 j' },
                  { k: 'plus_90', label: '+90 j' },
                ].map((bucket) => (
                  <div key={bucket.k} className="bg-gray-50 rounded-lg p-2">
                    <p className="text-xs text-gray-400">{bucket.label}</p>
                    <p className="text-sm font-semibold text-gray-800">{formatCurrency(num(b?.[bucket.k]))}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Historique dépenses */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Dépenses ({data.depenses?.length || 0})</h4>
              <div className="space-y-1.5">
                {(data.depenses || []).map((d: any) => (
                  <div key={d.id} className="flex items-center justify-between text-sm border-b border-gray-50 pb-1.5">
                    <div>
                      <p className="text-gray-800">{d.numero} — {d.description}</p>
                      <p className="text-xs text-gray-400">{d.date_depense} · {d.statut}</p>
                    </div>
                    <span className="font-medium text-gray-700">{formatCurrency(num(d.montant))}</span>
                  </div>
                ))}
                {(data.depenses || []).length === 0 && <p className="text-sm text-gray-400">Aucune dépense.</p>}
              </div>
            </div>

            {/* Paiements */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                <Banknote className="h-4 w-4" /> Paiements ({data.paiements?.length || 0})
              </h4>
              <div className="space-y-1.5">
                {(data.paiements || []).map((p: any) => (
                  <div key={p.id} className="flex items-center justify-between text-sm border-b border-gray-50 pb-1.5">
                    <p className="text-xs text-gray-500">{p.date_paiement} · {p.mode_paiement}{p.reference ? ` · ${p.reference}` : ''}</p>
                    <span className="font-medium text-green-700">{formatCurrency(num(p.montant))}</span>
                  </div>
                ))}
                {(data.paiements || []).length === 0 && <p className="text-sm text-gray-400">Aucun paiement.</p>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FournisseursTiers;
